import React, { useCallback } from 'react'
import PropertyTypeSelector from './PropertyTypeSelector';

interface Option {
    key: string;
    value: string;
    description: string;
    icon: string;
}

interface PropertyTypeOption extends Option {}



interface BasicDetailSectionProps {
    formData: {
        basicDetails: {
            lookingTo: string;
            propertyType: string;
            officeType?: PropertyTypeOption;
            retailSpaceType?: PropertyTypeOption;
            retailLocationInside?: PropertyTypeOption;
            plotLandType?: PropertyTypeOption;
            storageType?: PropertyTypeOption;
            industryType?: PropertyTypeOption;
            hospitalityType?: PropertyTypeOption;
            additionalOption?: PropertyTypeOption;
        };
    };
    handleChange: (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => void;
    setFormData:React.Dispatch<React.SetStateAction<any>>; // Add setFormData as a prop

}

const BasicDetailSection: React.FC<BasicDetailSectionProps> = ({ formData, handleChange ,setFormData }) => {

    const SellResidential = [
        { key: "flat_apartment", value: "Flat/Apartment", description: "A unit in a multi-story building", icon: "🏢" },
        { key: "independent_villa", value: "Independent House/Villa", description: "A standalone house with its own land", icon: "🏡" },
        { key: "independent_builder", value: "Independent/Builder Floor", description: "Separate floors in a house", icon: "🏠" },
        { key: "plot_land", value: "Plot/Land", description: "A piece of land for construction or investment", icon: "🌍" },
        { key: "studio_apartment", value: "1 RK / Studio Apartment", description: "A compact unit with one room and kitchen", icon: "🛏️" },
        { key: "serviced_apartment", value: "Serviced Apartment", description: "Furnished apartment with services", icon: "🏨" },
        { key: "farm_house", value: "Farm House", description: "A house in a rural area", icon: "🌾" },
        { key: "other", value: "Other", description: "Any other type of property", icon: "❓" },
    ];

    const SellCommercial = [
        { key: "office", value: "Office", description: "Commercial office space", icon: "🏢" },
        { key: "retail", value: "Retail", description: "Retail space", icon: "🏪" },
        { key: "plot_land", value: "Plot/Land", description: "Commercial land", icon: "🌍" },
        { key: "storage", value: "Storage", description: "Warehouse or storage", icon: "🏗️" },
        { key: "industry", value: "Industry", description: "Industrial property", icon: "🏭" },
        { key: "hospitality", value: "Hospitality", description: "Hotels or resorts", icon: "🏨" },
        { key: "other", value: "Other Commercial", description: "Other commercial property", icon: "❓" },
    ];


    const PG = [
        { key: "pg", value: "PG", description: "Paying Guest accommodation", icon: "🏠" },
    ];


    const AGRICULTURE = [
        { key: "plot_land", value: "Plot/Land", description: "Paying Guest accommodation", icon: "🏠" },
    ];


    const kindOffice = [
        { key: "bare_shell_office", value: "Bare Shell Office", description: "Paying Guest accommodation", icon: "🏠" },
        { key: "co_working_office", value: "Co-Working Office", description: "Paying Guest accommodation", icon: "🏠" },
        { key: "ready_to_move_office", value: "Ready to move office space", description: "Paying Guest accommodation", icon: "🏠" },
    ]


    const spaceTypes = [
        { key: "commericial_shop", value: "Commericial Shop", description: "Paying Guest accommodation", icon: "🏠" },
        { key: "commericial_showrooms", value: "Commericial Showrooms", description: "Paying Guest accommodation", icon: "🏠" },
    ]

    const shopInsides = [
        { key: "mall", value: "Mall", description: "Paying Guest accommodation", icon: "🏠" },
        { key: "commercial_project", value: "Commercial Project", description: "Paying Guest accommodation", icon: "🏠" },
        { key: "residential_project", value: "Residential Project", description: "Paying Guest accommodation", icon: "🏠" },
        { key: "retail_complex", value: "Retail Complex/Building", description: "Paying Guest accommodation", icon: "🏠" },
        { key: "market_high_street", value: "Market / High Street", description: "Paying Guest accommodation", icon: "🏠" },
        { key: "others", value: "Others", description: "Paying Guest accommodation", icon: "🏠" },
    ];



    // select plot_land

    const plotLandTypes = [
        { key: "commercial_land", value: "Commercial Land", description: "Paying Guest accommodation", icon: "🏠" },
        { key: "industrial_land", value: "Industrial Land", description: "Paying Guest accommodation", icon: "🏠" },
        { key: "agricultural_farm_land", value: "Agricultural / Farm Land", description: "Paying Guest accommodation", icon: "🏠" },
    ];

    // select industry
    const industryTypes = [
        { key: "ware_house", value: "Ware House", description: "Paying Guest accommodation", icon: "🏠" },
        { key: "manufacturing", value: "Manufacturing", description: "Paying Guest accommodation", icon: "🏠" },
    ];

    // select storage
    const storageTypes = [
        { key: "factory", value: "Factory", description: "Paying Guest accommodation", icon: "🏠" },
        { key: "cold_storage", value: "Cold Storage", description: "Paying Guest accommodation", icon: "🏠" },
    ];

    // select hospitality
    const hospitalityTypes = [
        { key: "hotel_resorts", value: "Hotel / Resorts", description: "Paying Guest accommodation", icon: "🏠" },
        { key: "guest_house_banquet_halls", value: "Guest House / Banquet-Halls", description: "Paying Guest accommodation", icon: "🏠" },
    ];

    const getOptions = () => {
        if (formData.basicDetails.lookingTo === "PG") {
            return PG;
        }
        else if (formData?.basicDetails?.propertyType === "agriculture") {
            return AGRICULTURE;
        }
        else if (formData.basicDetails.lookingTo === "Sell" && formData.basicDetails.propertyType === "Residential") {
            return SellResidential;
        } else if (formData.basicDetails.lookingTo === "Sell" && formData.basicDetails.propertyType === "Commercial") {
            return SellCommercial;
        } else if (formData.basicDetails.lookingTo === "Rent / Lease") {
            return formData.basicDetails.propertyType === "Residential" ? SellResidential : SellCommercial;
        }
        return [];
    };



    const handlePropertyTypeChange = useCallback((selectedOption) => {
        setFormData((prevState) => {
            if (prevState?.basicDetails?.additionalOption?.key !== selectedOption.key) {
                return {
                    ...prevState,
                    basicDetails: {
                        ...prevState.basicDetails, // Preserve other fields in basicDetails
                        additionalOption: selectedOption
                    },
                };
            }
            return prevState; // Prevent update if selected option is same as the previous one
        });
    }, []);


    return (
        <fieldset

            className="border border-gray-300 p-4 rounded-md">
            <legend className="text-lg font-semibold px-2">Basic Details</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">I'm Looking To</label>
                    <select
                        name="basicDetails.lookingTo"
                        value={formData.basicDetails.lookingTo}
                        onChange={handleChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                    >
                        <option value="Sell">Sell</option>
                        <option value="Rent / Lease">Rent / Lease</option>
                        <option value="PG">PG</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">What kind of property do you have?</label>
                    <select
                        name="basicDetails.propertyType"
                        value={formData.basicDetails.propertyType}
                        onChange={handleChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                        disabled={formData.basicDetails.lookingTo === "PG"}
                    >
                        <option value="Residential">Residential</option>
                        <option value="Commercial">Commercial</option>
                        <option value="agriculture">Agriculture</option>
                    </select>
                </div>
            </div>
            <PropertyTypeSelector
                options={getOptions()}
                onSelectionChange={handlePropertyTypeChange}
            />


            {(formData.basicDetails.lookingTo !== "PG" && formData.basicDetails.propertyType == "Commercial" && formData.basicDetails?.additionalOption?.key == "office") &&
                <div>
                    <label htmlFor="" className="block text-md font-medium text-black">What kind of office is it?</label>
                    <div className="flex flex-wrap gap-4 p-4">
                        {kindOffice.map((option) => (
                            <button
                                type="button"
                                key={option.key}
                                onClick={() => {
                                    // Update officeType in formData
                                    setFormData((prevData) => ({
                                        ...prevData,
                                        basicDetails: {
                                            ...prevData.basicDetails,
                                            officeType: option, // Set the office type key
                                        },
                                    }));
                                }}
                                className={`px-4 py-2 rounded border transition-all ${formData.basicDetails?.officeType?.key === option.key
                                    ? "bg-green-500 text-white border-green-500"
                                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                    }`}
                            >
                                {option.value}
                            </button>
                        ))}
                    </div>
                </div>
            }


            {(formData.basicDetails.lookingTo !== "PG" && formData.basicDetails.propertyType == "Commercial" && formData.basicDetails?.additionalOption?.key == "retail") &&
                <>
                    <div>
                        <label htmlFor="" className="block text-md font-medium text-black">What type of space do you have?</label>
                        <div className="flex flex-wrap gap-4 p-4">
                            {spaceTypes.map((option) => (
                                <button
                                    type="button"
                                    key={option.key}
                                    onClick={() => {
                                        // Update officeType in formData
                                        setFormData((prevData) => ({
                                            ...prevData,
                                            basicDetails: {
                                                ...prevData.basicDetails,
                                                retailSpaceType: option, // Set the office type key
                                            },
                                        }));
                                    }}
                                    className={`px-4 py-2 rounded border transition-all ${formData.basicDetails?.retailSpaceType?.key === option.key
                                        ? "bg-green-500 text-white border-green-500"
                                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                        }`}
                                >
                                    {option.value}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label htmlFor="" className="block text-md font-medium text-black">Your Shop is located inside?</label>
                        <div className="flex flex-wrap gap-4 p-4">
                            {shopInsides.map((option) => (
                                <button
                                    type="button"
                                    key={option.key}
                                    onClick={() => {
                                        // Update officeType in formData
                                        setFormData((prevData) => ({
                                            ...prevData,
                                            basicDetails: {
                                                ...prevData.basicDetails,
                                                retailLocationInside: option, // Set the office type key
                                            },
                                        }));
                                    }}
                                    className={`px-4 py-2 rounded border transition-all ${formData.basicDetails?.retailLocationInside?.key === option.key
                                        ? "bg-green-500 text-white border-green-500"
                                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                        }`}
                                >
                                    {option.value}
                                </button>
                            ))}
                        </div>
                    </div>
                </>
            }


            {(formData.basicDetails.lookingTo !== "PG" && formData.basicDetails.propertyType == "Commercial" && formData.basicDetails?.additionalOption?.key == "plot_land") &&
                <>
                    <div>
                        <label htmlFor="" className="block text-md font-medium text-black">What type of plot/land is it?</label>
                        <div className="flex flex-wrap gap-4 p-4">
                            {plotLandTypes.map((option) => (
                                <button
                                    type="button"
                                    key={option.key}
                                    onClick={() => {
                                        // Update officeType in formData
                                        setFormData((prevData) => ({
                                            ...prevData,
                                            basicDetails: {
                                                ...prevData.basicDetails,
                                                plotLandType: option, // Set the office type key
                                            },
                                        }));
                                    }}
                                    className={`px-4 py-2 rounded border transition-all ${formData.basicDetails?.plotLandType?.key === option.key
                                        ? "bg-green-500 text-white border-green-500"
                                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                        }`}
                                >
                                    {option.value}
                                </button>
                            ))}
                        </div>
                    </div>
                </>
            }



            {(formData.basicDetails.lookingTo !== "PG" && formData.basicDetails.propertyType == "Commercial" && formData.basicDetails?.additionalOption?.key == "storage") &&
                <>
                    <div>
                        <label htmlFor="" className="block text-md font-medium text-black">What kind of Storage is it?</label>
                        <div className="flex flex-wrap gap-4 p-4">
                            {storageTypes.map((option) => (
                                <button
                                    type="button"
                                    key={option.key}
                                    onClick={() => {
                                        // Update officeType in formData
                                        setFormData((prevData) => ({
                                            ...prevData,
                                            basicDetails: {
                                                ...prevData.basicDetails,
                                                storageType: option, // Set the office type key
                                            },
                                        }));
                                    }}
                                    className={`px-4 py-2 rounded border transition-all ${formData.basicDetails?.storageType?.key === option.key
                                        ? "bg-green-500 text-white border-green-500"
                                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                        }`}
                                >
                                    {option.value}
                                </button>
                            ))}
                        </div>
                    </div>
                </>
            }


            {(formData.basicDetails.lookingTo !== "PG" && formData.basicDetails.propertyType == "Commercial" && formData.basicDetails?.additionalOption?.key == "industry") &&
                <>
                    <div>
                        <label htmlFor="" className="block text-md font-medium text-black">What kind of industry is it?</label>
                        <div className="flex flex-wrap gap-4 p-4">
                            {industryTypes.map((option) => (
                                <button
                                    type="button"
                                    key={option.key}
                                    onClick={() => {
                                        // Update officeType in formData
                                        setFormData((prevData) => ({
                                            ...prevData,
                                            basicDetails: {
                                                ...prevData.basicDetails,
                                                industryType: option, // Set the office type key
                                            },
                                        }));
                                    }}
                                    className={`px-4 py-2 rounded border transition-all ${formData.basicDetails?.industryType?.key === option.key
                                        ? "bg-green-500 text-white border-green-500"
                                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                        }`}
                                >
                                    {option.value}
                                </button>
                            ))}
                        </div>
                    </div>
                </>
            }


            {(formData.basicDetails.lookingTo !== "PG" && formData.basicDetails.propertyType == "Commercial" && formData.basicDetails?.additionalOption?.key == "hospitality") &&
                <>
                    <div>
                        <label htmlFor="" className="block text-md font-medium text-black">What kind of hospitalityType is it?</label>
                        <div className="flex flex-wrap gap-4 p-4">
                            {hospitalityTypes.map((option) => (
                                <button
                                    type="button"
                                    key={option.key}
                                    onClick={() => {
                                        // Update officeType in formData
                                        setFormData((prevData) => ({
                                            ...prevData,
                                            basicDetails: {
                                                ...prevData.basicDetails,
                                                hospitalityType: option, // Set the office type key
                                            },
                                        }));
                                    }}
                                    className={`px-4 py-2 rounded border transition-all ${formData.basicDetails?.hospitalityType?.key === option.key
                                        ? "bg-green-500 text-white border-green-500"
                                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                        }`}
                                >
                                    {option.value}
                                </button>
                            ))}
                        </div>
                    </div>
                </>
            }




        </fieldset>
    )
}

export default BasicDetailSection